/**
 * MongoDB Architectural Analysis Repository
 * 
 * Infrastructure adapter implementing repository pattern for ArchitecturalAnalysis aggregate.
 * Provides persistence capabilities using MongoDB with proper error handling.
 */

import { Collection, getMongoDb } from "@swoft/persistence";
import { ArchitecturalAnalysis, ArchitecturalAnalysisData } from '../../domain/entities/ArchitecturalAnalysis';
import type { IArchitecturalAnalysisRepository } from '../../application/services/BoundedContextAnalysisService';

export class MongoArchitecturalAnalysisRepository implements IArchitecturalAnalysisRepository {
  private collection?: Collection<ArchitecturalAnalysisData>;

  private getCollection(): Collection<ArchitecturalAnalysisData> {
    if (!this.collection) {
      const db = getMongoDb();
      this.collection = db.collection<ArchitecturalAnalysisData>('architecturalAnalyses');
    }
    return this.collection;
  }

  async save(analysis: ArchitecturalAnalysis): Promise<void> {
    try {
      const data = analysis.toData();
      
      const collection = this.getCollection();
      await collection.replaceOne(
        { id: data.id },
        data,
        { upsert: true }
      );
    } catch (error) {
      throw new Error(`Failed to save architectural analysis: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async findById(id: string): Promise<ArchitecturalAnalysis | null> {
    try {
      const collection = this.getCollection();
      const data = await collection.findOne({ id });
      
      return data ? ArchitecturalAnalysis.fromData(data) : null;
    } catch (error) {
      throw new Error(`Failed to find architectural analysis by id: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async findLatest(): Promise<ArchitecturalAnalysis | null> {
    try {
      const data = await this.collection.findOne(
        {},
        { sort: { analysisCompletedAt: -1 } }
      );
      
      return data ? ArchitecturalAnalysis.fromData(data) : null;
    } catch (error) {
      throw new Error(`Failed to find latest architectural analysis: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async findAll(): Promise<ArchitecturalAnalysis[]> {
    try {
      const cursor = this.collection.find({});
      const data = await cursor.toArray();
      
      return data.map(d => ArchitecturalAnalysis.fromData(d));
    } catch (error) {
      throw new Error(`Failed to find all architectural analyses: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}